import { createFileRoute } from '@tanstack/react-router'
import { BarChart3, Clock, CheckCircle, AlertTriangle } from 'lucide-react'
import { useState } from 'react'

import { DateRangePicker } from '@/components/ui/date-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useWorkloadStats,
  useProjects,
  useTeams,
  type WorkloadStatsQuery,
  type StatsDeveloperGroup as DeveloperGroup,
  DEVELOPER_GROUPS,
} from '@/hooks/pm'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/pm/workload-stats')({
  component: WorkloadStatsPage,
})

const DEVELOPER_GROUP_LABELS: Record<DeveloperGroup, string> = {
  product: '产品组',
  design: '美术组',
  frontend: '前端组',
  backend: '后端组',
  testing: '测试组',
}

const DEVELOPER_GROUP_COLORS: Record<DeveloperGroup, string> = {
  product: 'bg-purple-500',
  design: 'bg-pink-500',
  frontend: 'bg-blue-500',
  backend: 'bg-green-500',
  testing: 'bg-orange-500',
}

function isDeveloperGroup(group: string): group is DeveloperGroup {
  return (DEVELOPER_GROUPS as readonly string[]).includes(group)
}

function WorkloadStatsPage() {
  const [filters, setFilters] = useState<WorkloadStatsQuery>({})

  const { data: stats, isLoading, error } = useWorkloadStats(filters)
  const { data: projects } = useProjects()
  const { data: teams } = useTeams()

  // 按开发组分组统计
  const groupStats = stats?.members.reduce(
    (acc, member) => {
      const group = member.developerGroup || 'unknown'
      if (!acc[group]) {
        acc[group] = { count: 0, hours: 0 }
      }
      acc[group].count++
      acc[group].hours += member.hours.estimated
      return acc
    },
    {} as Record<string, { count: number; hours: number }>
  )

  // 错误消息将在筛选器之后显示，而不是直接返回

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">工作量统计</h1>
        <p className="text-muted-foreground">查看团队成员的任务分配和工作量情况</p>
      </div>

      {/* 筛选 */}
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <Select
            value={filters.projectId ?? 'all'}
            onValueChange={(value) =>
              setFilters((prev) => ({ ...prev, projectId: value === 'all' ? undefined : value }))
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="全部项目" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部项目</SelectItem>
              {projects?.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Select
            value={filters.teamId ?? 'all'}
            onValueChange={(value) =>
              setFilters((prev) => ({ ...prev, teamId: value === 'all' ? undefined : value }))
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="全部团队" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部团队</SelectItem>
              {teams?.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DateRangePicker
          startDate={filters.startDate}
          endDate={filters.endDate}
          onStartDateChange={(date) => setFilters((prev) => ({ ...prev, startDate: date }))}
          onEndDateChange={(date) => setFilters((prev) => ({ ...prev, endDate: date }))}
        />
      </div>

      {error ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">请选择筛选条件查看工作量统计</div>
        </div>
      ) : isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : !stats ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">暂无数据</div>
        </div>
      ) : (
        <>
          {/* 汇总统计 */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border p-4">
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <BarChart3 className="h-4 w-4" />
                总任务数
              </div>
              <div className="mt-2 text-2xl font-bold">{stats.summary.totalTasks}</div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4" />
                预估工时
              </div>
              <div className="mt-2 text-2xl font-bold">{stats.summary.totalEstimatedHours}h</div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4" />
                实际工时
              </div>
              <div className="mt-2 text-2xl font-bold">{stats.summary.totalActualHours}h</div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4" />
                完成率
              </div>
              <div className="mt-2 text-2xl font-bold">
                {stats.summary.totalTasks > 0
                  ? Math.round(
                    (stats.members.reduce((acc, m) => acc + m.taskCount.done, 0) /
                      stats.summary.totalTasks) *
                    100
                  )
                  : 0}
                %
              </div>
            </div>
          </div>

          {/* 成员工作量表格 */}
          <div className="rounded-lg border">
            <div className="bg-muted/50 border-b px-4 py-3">
              <h3 className="font-medium">成员工作量</h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-muted/30 border-b">
                  <th className="px-4 py-3 text-left text-sm font-medium">成员</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">开发组</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">总任务</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">待办</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">进行中</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">已完成</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">预估工时</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                </tr>
              </thead>
              <tbody>
                {stats.members.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-muted-foreground px-4 py-8 text-center">
                      暂无成员数据
                    </td>
                  </tr>
                ) : (
                  stats.members.map((member) => (
                    <tr
                      key={member.employeeId}
                      className="hover:bg-muted/30 border-b last:border-0"
                    >
                      <td className="px-4 py-3 font-medium">{member.employeeName}</td>
                      <td className="px-4 py-3 text-sm">
                        {member.developerGroup ? (
                          <span className="inline-flex items-center gap-1">
                            <span
                              className={cn(
                                'h-2 w-2 rounded-full',
                                DEVELOPER_GROUP_COLORS[member.developerGroup]
                              )}
                            />
                            {DEVELOPER_GROUP_LABELS[member.developerGroup]}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{member.taskCount.total}</td>
                      <td className="px-4 py-3 text-sm">{member.taskCount.todo}</td>
                      <td className="px-4 py-3 text-sm">{member.taskCount.inProgress}</td>
                      <td className="px-4 py-3 text-sm">{member.taskCount.done}</td>
                      <td className="px-4 py-3 text-sm">{member.hours.estimated}h</td>
                      <td className="px-4 py-3">
                        {member.isHighWorkload ? (
                          <span className="inline-flex items-center gap-1 text-yellow-600">
                            <AlertTriangle className="h-4 w-4" />
                            高负载
                          </span>
                        ) : member.hours.estimated < 16 ? (
                          <span className="text-blue-600">🔵 低负载</span>
                        ) : (
                          <span className="text-green-600">✅ 正常</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* 开发组工作量分布 */}
          {groupStats && Object.keys(groupStats).length > 0 && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-4 font-medium">开发组工作量分布</h3>
              <div className="space-y-3">
                {Object.entries(groupStats)
                  .filter(([group]) => group !== 'unknown')
                  .sort((a, b) => b[1].hours - a[1].hours)
                  .map(([group, data]) => {
                    const maxHours = Math.max(...Object.values(groupStats).map((d) => d.hours))
                    const percentage = maxHours > 0 ? (data.hours / maxHours) * 100 : 0
                    return (
                      <div key={group} className="flex items-center gap-4">
                        <div className="w-20 text-sm">
                          {isDeveloperGroup(group) ? DEVELOPER_GROUP_LABELS[group] : group}
                        </div>
                        <div className="flex-1">
                          <div className="bg-muted h-6 rounded-full">
                            <div
                              className={cn(
                                'h-full rounded-full transition-all',
                                isDeveloperGroup(group)
                                  ? DEVELOPER_GROUP_COLORS[group]
                                  : 'bg-gray-500'
                              )}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                        <div className="w-24 text-right text-sm">
                          {data.hours}h ({data.count}人)
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}

          {/* 状态说明 */}
          <div className="text-muted-foreground text-sm">
            状态说明: ⚠️ 高负载 (&gt;40h/周) &nbsp; ✅ 正常 (16-40h) &nbsp; 🔵 低负载 (&lt;16h/周)
          </div>
        </>
      )}
    </div>
  )
}
